/**
 * Internal rendering DTO and stored-v1 compatibility schema.
 * New packs enter through VisualPack and PackCatalog, never through this schema alone.
 */
import { z } from "zod";
import { UiColors } from "./color-registry.js";
import { VisualPack } from "./visual-pack.js";
export * from "./color-registry.js";
export * from "./scenery.js";
export * from "./visual-pack.js";
const Color = z.string().regex(/^(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\))$/, "CSS color");
const ButtonPalette = z.object({ start: Color, end: Color, shadow: Color });
const Graduations = z.object({
    color: Color,
    radius: z.number().min(2).max(4).default(2.5),
    minorColor: Color.optional(),
    textColor: Color.optional(),
});
export const AssetPath = z
    .string()
    .regex(/^[a-z0-9_-]+(?:\/[a-z0-9_.-]+)*\.(svg|png|webp|ogg|mp3|json)$/i, "relative asset path")
    .refine((path) => !path.split("/").some((part) => part === "." || part === ".."), "asset must stay inside its theme");
export const ThemeSlug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const ThemeVersion = z
    .string()
    .max(60)
    .regex(/^\d+\.\d+\.\d+$/);
/** Spritesheet filenames carry their metadata: {asset}_{action}_f{n}_{w}x{h}_g{c}x{r}_fps{n}_{loop|once}.png */
export const SPRITESHEET_NAME_REGEX = /^(?<asset>[a-z0-9-]+)_(?<action>[a-z0-9-]+)_f(?<frames>\d+)_(?<w>\d+)x(?<h>\d+)_g(?<cols>\d+)x(?<rows>\d+)_fps(?<fps>\d+)_(?<loop>loop|once)\.png$/;
export function parseSpritesheetName(fileName) {
    const m = SPRITESHEET_NAME_REGEX.exec(fileName);
    if (!m?.groups)
        return null;
    const g = m.groups;
    return {
        asset: g["asset"],
        action: g["action"],
        frames: Number(g["frames"]),
        cellWidth: Number(g["w"]),
        cellHeight: Number(g["h"]),
        cols: Number(g["cols"]),
        rows: Number(g["rows"]),
        fps: Number(g["fps"]),
        loop: g["loop"] === "loop",
    };
}
export const ThemeTokens = z.object({
    color: z.object({
        ui: UiColors.optional(),
        bg: Color,
        surface: Color,
        primary: Color,
        accent: Color,
        success: Color,
        warning: Color,
        danger: Color,
        text: Color,
        muted: Color,
        /** history badge colours by tier */
        history: z.object({ low: Color, mid: Color, high: Color }),
        /** vehicle recolour slots (used by SVG vehicles) */
        vehicle: z.object({
            primary: Color,
            secondary: Color,
            accent: Color,
            highlight: Color.optional(),
            shadow: Color.optional(),
            outline: Color.optional(),
        }),
        bet: ButtonPalette.default({ start: "#1fd07c", end: "#128f5c", shadow: "#0b6b3c" }),
        cashout: ButtonPalette.default({ start: "#ffa93a", end: "#f07d09", shadow: "#a85405" }),
    }),
    font: z.object({ ui: z.string().min(1), display: z.string().min(1) }),
    radius: z.string().default("12px"),
    shadow: z.string().default("0 8px 30px rgba(0,0,0,.4)"),
});
/** A vehicle is either an SVG (recolourable) or a spritesheet PNG. */
export const Vehicle = z.union([
    z.object({ type: z.literal("svg"), src: AssetPath, scale: z.number().positive().default(1) }),
    z.object({ type: z.literal("spritesheet"), src: AssetPath, scale: z.number().positive().default(1) }),
]);
const VehicleSelection = {
    activeVehicle: ThemeSlug,
    vehicles: z
        .record(ThemeSlug, Vehicle)
        .refine((vehicles) => Object.keys(vehicles).length > 0 && Object.keys(vehicles).length <= 32, "one to 32 vehicles required"),
};
function hasActiveVehicle(mode) {
    return Object.hasOwn(mode.vehicles, mode.activeVehicle);
}
export function activeVehicle(mode) {
    const vehicle = Object.hasOwn(mode.vehicles, mode.activeVehicle)
        ? mode.vehicles[mode.activeVehicle]
        : undefined;
    if (!vehicle)
        throw new Error("Missing active vehicle");
    return vehicle;
}
export const PlaneMode = z
    .object({
    ...VehicleSelection,
    background: z.object({
        type: z.enum(["rays", "image", "gradient"]).default("rays"),
        image: AssetPath.optional(),
        rays: z
            .object({
            color: Color,
            count: z.number().int().min(4).max(64).default(16),
            speed: z.number().default(0.02),
        })
            .optional(),
        gradient: z.array(Color).min(2).optional(),
    }),
    curve: z.object({
        stroke: Color,
        fill: Color,
        fillEnd: Color.optional(),
        width: z.number().positive().default(4),
    }),
    graduations: Graduations.optional(),
    clouds: z
        .object({
        type: z.enum(["procedural", "sprite"]).default("procedural"),
        src: AssetPath.optional(),
        color: Color.optional(),
    })
        .optional(),
    trail: z.object({ color: Color, length: z.number().positive().default(120) }).optional(),
})
    .refine(hasActiveVehicle, "active vehicle must exist in the mode catalogue");
export const RocketMode = z
    .object({
    ...VehicleSelection,
    /** Parallax layers from bottom (sky) to top (space). speed = scroll factor. */
    layers: z
        .array(z.object({
        type: z.enum(["gradient", "image", "stars"]),
        gradient: z.array(Color).min(2).optional(),
        image: AssetPath.optional(),
        stars: z
            .object({
            color: Color,
            density: z.number().positive().default(1),
            seed: z.number().int().default(1),
        })
            .optional(),
        speed: z.number().nonnegative().default(1),
    }))
        .min(1),
    particles: z.object({ flame: Color, flameEnd: Color.optional(), smoke: Color, debris: Color.optional() }),
    graduations: Graduations.optional(),
    scenery: z.object({ terrain: Color, clouds: Color, seed: z.number().int() }).optional(),
    explosion: z
        .object({
        type: z.enum(["procedural", "spritesheet"]).default("procedural"),
        src: AssetPath.optional(),
    })
        .optional(),
    shake: z.number().min(0).max(3).default(1),
})
    .refine(hasActiveVehicle, "active vehicle must exist in the mode catalogue");
export const ThemeAudio = z.object({
    engine: AssetPath.optional(),
    takeoff: AssetPath.optional(),
    tick: AssetPath.optional(),
    cashout: AssetPath.optional(),
    crash: AssetPath.optional(),
    music: AssetPath.optional(),
    /** When true, missing files are synthesised at runtime (jsfxr-style). */
    synthFallback: z.boolean().default(true),
});
export const ThemeManifest = z.object({
    visual: VisualPack.optional(),
    $schema: z.string().optional(),
    slug: ThemeSlug,
    name: z.string().min(1).max(60),
    version: ThemeVersion,
    author: z.string().min(1).max(80),
    /** Must be true for packs shipped in the CodeCanyon ZIP (no AI-generated assets). */
    license: z.object({
        assets: z.enum(["crashx", "original-vector", "cc0", "mixed-original-cc0", "external-pack"]),
        credits: z.string().optional(),
    }),
    tokens: ThemeTokens,
    modes: z
        .object({ plane: PlaneMode.optional(), rocket: RocketMode.optional() })
        .refine((m) => m.plane || m.rocket, {
        message: "a theme must define at least one mode",
    }),
    audio: ThemeAudio.default({ synthFallback: true }),
    ui: z.object({
        logo: AssetPath,
        avatars: z
            .object({ type: z.enum(["generated", "folder"]).default("generated"), folder: z.string().optional() })
            .default({ type: "generated" }),
        multiplierStyle: z.enum(["plain", "outline", "glow"]).default("outline"),
    }),
});
const EditorColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "six-digit hexadecimal color");
export const VehicleColors = z.strictObject({
    primary: EditorColor,
    secondary: EditorColor,
    accent: EditorColor,
});
export const ButtonColors = z.strictObject({
    bet: ButtonPalette,
    cashout: ButtonPalette,
});
export const ThemeSelection = z.strictObject({
    slug: ThemeSlug,
    vehicles: z.strictObject({ plane: ThemeSlug.optional(), rocket: ThemeSlug.optional() }).default({}),
    vehicleColors: VehicleColors.optional(),
    buttonColors: ButtonColors.optional(),
});
export const ActiveThemeResponse = z.object({
    manifest: ThemeManifest,
    assetBaseUrl: z.string(),
    builtin: z.boolean(),
});
export const ThemeListEntry = z.object({
    slug: ThemeSlug,
    name: z.string(),
    version: z.string(),
    builtin: z.boolean(),
});
export const ThemeVersionList = z.object({
    limit: z.number().int().positive(),
    versions: z.array(z.object({ id: z.uuid(), version: z.string(), createdAt: z.number(), current: z.boolean() })),
});
export const ThemeRollbackInput = z.strictObject({ versionId: z.uuid() });
export const ThemeChangeReport = z.object({
    slug: ThemeSlug,
    fromVersion: z.string(),
    toVersion: z.string(),
    changed: z.array(z.string()),
    preserved: z.array(z.string()),
    reset: z.array(z.string()),
    assets: z
        .object({ added: z.array(z.string()), modified: z.array(z.string()), removed: z.array(z.string()) })
        .default({ added: [], modified: [], removed: [] }),
});
export function parseThemeManifest(json) {
    return ThemeManifest.parse(json);
}
export function safeParseThemeManifest(json) {
    return ThemeManifest.safeParse(json);
}
//# sourceMappingURL=runtime-manifest.js.map